import { useEffect, useMemo, useState } from "react";
// import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
// import Select from "react-select";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
// import { Input } from "@/components/ui/input";
import { organizationsApi, usageApi } from "@/apis";
import type { Organization, UsageAppItem, UsageUserSummary } from "@/apis";

interface AppUsageUsersProps {
  appId: string;
}

// Static tab definitions
const TABS = [
  { id: "users", label: "Users" },
  { id: "companies", label: "Companies" },
  { id: "simulations", label: "Simulations" },
] as const;

type TabId = (typeof TABS)[number]["id"];

// Static column definitions for each tab
const USER_COLUMNS = [
  { key: "user_name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "prospect_company_count", label: "Prospect Companies Ingested" },
  { key: "seller_company_count", label: "Seller Companies Ingested" },
  { key: "simulation_count", label: "Simulations" },
  // { key: "last_activity_at", label: "Last Activity", format: "datetime" },
];

const COMPANY_COLUMNS = [
  { key: "company_name", label: "Company Name" },
  { key: "website", label: "Website" },
  { key: "user_name", label: "User" },
  { key: "entity_key", label: "Entity" },
  { key: "last_activity", label: "Ingested At", format: "datetime" },
];

const SIMULATION_COLUMNS = [
  { key: "user_name", label: "User" },
  { key: "seller_company", label: "Seller Company" },
  { key: "prospect_company", label: "Prospect Company" },
  { key: "last_activity", label: "Simulation Started At", format: "datetime" },
];

function formatCellValue(value: unknown, format?: string): string {
  if (value === null || value === undefined || value === "") return "-";
  if (format === "datetime" && typeof value === "string") {
    return new Date(value).toLocaleString();
  }
  return String(value);
}

export function AppUsageUsers({ appId }: AppUsageUsersProps) {
  const [app, setApp] = useState<UsageAppItem | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>("users");
  const [organizations, setOrganizations] = useState<Organization[]>([]);

  // Users tab state
  const [users, setUsers] = useState<any>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [organizationId, setOrganizationId] = useState<string>("");

  // Companies tab state
  const [companies, setCompanies] = useState<any>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesError, setCompaniesError] = useState("");
  const [companiesPage, setCompaniesPage] = useState(1);
  const [companiesTotal, setCompaniesTotal] = useState(0);

  // Simulations tab state
  const [simulations, setSimulations] = useState<any>([]);
  const [simulationsLoading, setSimulationsLoading] = useState(false);
  const [simulationsError, setSimulationsError] = useState("");
  const [simulationsPage, setSimulationsPage] = useState(1);
  const [simulationsTotal, setSimulationsTotal] = useState(0);

  const limit = 100;



  const loadUsers = async (page: number = 1) => {
    setUsersLoading(true);
    setUsersError("");
    try {
      const data = await usageApi.listUsers(appId, {
        page,
        limit,
        search: search || undefined,
        organization_id: organizationId || undefined,
      });
      setUsers(data?.users ?? []);
      setUsersTotal(data.total_count ?? 0);
      setUsersPage(data.page ?? page);
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : "Failed to load users");
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadCompanies = async (page: number = 1) => {
    setCompaniesLoading(true);
    setCompaniesError("");
    try {
      // Call the generic section endpoint for companies
      const data = await usageApi.getCompanies(appId,  {
        page,
        limit,
      });
      setCompanies(data.companies ?? []);
      setCompaniesTotal(data.total_count ?? 0);
      setCompaniesPage(data.page ?? page);
    } catch (err) {
      setCompaniesError(err instanceof Error ? err.message : "Failed to load companies");
      setCompanies([]);
    } finally {
      setCompaniesLoading(false);
    }
  };

  const loadSimulations = async (page: number = 1) => {
    setSimulationsLoading(true);
    setSimulationsError("");
    try {
      // Call the generic section endpoint for simulations
      const data = await usageApi.getSimulations(appId, {
        page,
        limit,
      });
      setSimulations(data.simulations ?? []);
      setSimulationsTotal(data.total_count ?? 0);
      setSimulationsPage(data.page ?? page);
    } catch (err) {
      setSimulationsError(err instanceof Error ? err.message : "Failed to load simulations");
      setSimulations([]);
    } finally {
      setSimulationsLoading(false);
    }
  };

  // Load organizations for filter
  useEffect(() => {
    const loadOrgs = async () => {
      try {
        const data = await organizationsApi.list({ page: 1, limit: 500 });
        setOrganizations(data.items ?? []);
      } catch {
        setOrganizations([]);
      }
    };
    void loadOrgs();
  }, []);

  // Load initial data
  useEffect(() => {
    void loadUsers(1);
  }, [appId]);

  // Load tab data when tab changes
  useEffect(() => {
    if (activeTab === "companies" && companies.length === 0 && !companiesLoading) {
      void loadCompanies(1);
    } else if (activeTab === "simulations" && simulations.length === 0 && !simulationsLoading) {
      void loadSimulations(1);
    }
  }, [activeTab]);

  const organizationOptions = useMemo(
    () =>
      organizations.map((org) => ({
        value: org.organizationId,
        label: org.organizationName,
      })),
    [organizations]
  );

  const applyFilters = () => {
    setSearch(searchInput.trim());
    void loadUsers(1);
  };

  const getRowValue = (row: Record<string, unknown>, key: string): unknown => {
    if (key in row) return row[key];
    // Check in metrics object
    const metrics = row.metrics as Record<string, unknown> | undefined;
    if (metrics && key in metrics) return metrics[key];
    return undefined;
  };

  const renderTable = (
    columns: { key: string; label: string; format?: string }[],
    rows: Record<string, unknown>[],
    loading: boolean,
    error: string,
    emptyMessage: string
  ) => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      );
    }

    return (
      <div className="overflow-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              {columns.map((col) => (
                <th key={col.key} className="px-3 py-2 text-left font-medium">
                  {col.label}
                </th>
              ))}
              {/* {activeTab === "users" && <th className="px-3 py-2 text-left font-medium">Actions</th>} */}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (activeTab === "users" ? 1 : 0)}
                  className="px-3 py-6 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr key={String(row.id || row.user_id || idx)} className="border-t">
                  {columns.map((col) => (
                    <td key={col.key} className="px-3 py-2">
                      {formatCellValue(getRowValue(row, col.key), col.format)}
                    </td>
                  ))}
                  {/* {activeTab === "users" && (
                    <td className="px-3 py-2">
                      <Link
                        to={`/settings/usage/${appId}/users/${String(row.user_id || row.id)}`}
                        className="text-primary underline-offset-4 hover:underline"
                      >
                        View details
                      </Link>
                    </td>
                  )} */}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPagination = (
    page: number,
    total: number,
    onPageChange: (p: number) => void,
    loading: boolean
  ) => {
    const totalPages = Math.max(1, Math.ceil(total / limit));
    return (
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Page {page} of {totalPages} ({total} total)
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1 || loading}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages || loading}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* App Header with Static Metrics */}
      {app ? (
        <div className="rounded-lg border bg-card p-4">
          <div className="flex flex-wrap items-center gap-4">
            {app.icon ? (
              <img src={app.icon} alt={app.appName} className="h-10 w-10 rounded-md object-cover" />
            ) : null}
            <div className="flex-1">
              <h2 className="text-xl font-semibold">{app.appName}</h2>
            </div>
            {/* Static Summary Metrics */}
            <div className="flex gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold">{app.summary?.companies_ingested ?? 0}</p>
                <p className="text-xs text-muted-foreground">Companies Ingested</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{app.summary?.simulations_run ?? 0}</p>
                <p className="text-xs text-muted-foreground">Simulations</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{app.summary?.active_users ?? 0}</p>
                <p className="text-xs text-muted-foreground">Active Users</p>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Static Tabs */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex space-x-1 border-b">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? "border-b-2 border-primary text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (activeTab === "users") void loadUsers(usersPage);
                else if (activeTab === "companies") void loadCompanies(companiesPage);
                else void loadSimulations(simulationsPage);
              }}
              disabled={
                (activeTab === "users" && usersLoading) ||
                (activeTab === "companies" && companiesLoading) ||
                (activeTab === "simulations" && simulationsLoading)
              }
            >
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          {/* Users Tab with Filters */}
          {activeTab === "users" && (
            <>
              {/* <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[220px] flex-1">
                  <label className="mb-1 block text-sm font-medium">Search</label>
                  <Input
                    placeholder="Search by email"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") applyFilters();
                    }}
                  />
                </div>
                <div className="min-w-[220px]">
                  <label className="mb-1 block text-sm font-medium">Organization</label>
                  <Select
                    className="react-select-container"
                    classNamePrefix="react-select"
                    placeholder="All organizations"
                    isClearable
                    value={organizationOptions.find((o) => o.value === organizationId) ?? null}
                    onChange={(selected) => setOrganizationId(selected?.value ?? "")}
                    options={organizationOptions}
                  />
                </div>
                <Button onClick={applyFilters}>Apply</Button>
              </div> */}
              {renderTable(
                USER_COLUMNS,
                users,
                usersLoading,
                usersError,
                "No users found."
              )}
              {renderPagination(usersPage, usersTotal, (p) => void loadUsers(p), usersLoading)}
            </>
          )}

          {/* Companies Tab */}
          {activeTab === "companies" && (
            <>
              {renderTable(
                COMPANY_COLUMNS,
                companies,
                companiesLoading,
                companiesError,
                "No companies found."
              )}
              {renderPagination(companiesPage, companiesTotal, (p) => void loadCompanies(p), companiesLoading)}
            </>
          )}

          {/* Simulations Tab */}
          {activeTab === "simulations" && (
            <>
              {renderTable(
                SIMULATION_COLUMNS,
                simulations,
                simulationsLoading,
                simulationsError,
                "No simulations found."
              )}
              {renderPagination(simulationsPage, simulationsTotal, (p) => void loadSimulations(p), simulationsLoading)}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
